import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {NativeSelect} from '@/components/native-select'
import {SearchInput} from '@/components/ui/search-input'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {CeldaActo, CeldaEstado} from '@/components/ui/celdas-tabla'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DataTable, type Column} from '@/components/data-table'
import {ListEmptyState} from '@/components/section-card'
import {BUSINESSES_PAGE_SIZE, useBusinesses} from '@/hooks/empresa/organizacion/use-businesses'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {
  MEMBRESIA_SOBRE_CUPO,
  ROLES_MEMBRESIA,
  useAgregarMembresia,
  useCambiarEstadoMembresia,
  type Membresia,
  type RolMembresia,
  type Usuario,
} from '@/hooks/inci/consola/use-usuarios'
import {publicarExito} from '@/lib/avisos'
import {validateRequired} from '@/lib/validation'
import {SYSTEM_NIT} from '@/lib/constants'
import {detalleDelError, marcarErrorComoComunicado, translateApiError} from '@/lib/errors'

/** Explícito y no `${verbo}da`: esa plantilla arma «desactivarda» y `i18n-check.mjs` no ve una clave construida. */
const AVISO_DE: Record<'desactivar' | 'reactivar', 'desactivada' | 'reactivada'> = {
  desactivar: 'desactivada',
  reactivar: 'reactivada',
}

/** El 409 del cupo no es un rechazo sino la pregunta que falta; se marca como comunicado para que
 * `lib/red-errores-mutacion.ts` no saque una burbuja de error. */
function esSobreCupo(err: unknown): boolean {
  if (detalleDelError(err) !== MEMBRESIA_SOBRE_CUPO) return false
  marcarErrorComoComunicado(err)
  return true
}

/** Desactivar es reversible (KAI-230): la membresía arrastra datos con retención legal de veinte años.
 * La de plataforma no ofrece acto: modo dios lo concede la existencia de la fila y el backend da 409. */
export function MembresiasSeccion({usuario}: {usuario: Usuario}) {
  const {t} = useTranslation()
  const cambiar = useCambiarEstadoMembresia()
  const [confirmando, setConfirmando] = useState<Membresia | null>(null)
  const [sobreCupo, setSobreCupo] = useState<Membresia | null>(null)
  const [error, setError] = useState<string | null>(null)

  function mover(membresia: Membresia, verbo: 'desactivar' | 'reactivar', confirmar_sobre_cupo?: boolean) {
    setError(null)
    cambiar.mutate(
      {userId: usuario.id, employeeId: membresia.employee_id, verbo, confirmar_sobre_cupo},
      {
        onSuccess: () => {
          setConfirmando(null)
          setSobreCupo(null)
          const aviso = AVISO_DE[verbo]
          publicarExito(t(`usuarios.membresias.${aviso}.doneTitle`), t(`usuarios.membresias.${aviso}.doneBody`), {
            testId: `usuario-membresia-${aviso}`,
          })
        },
        onError: (err) => {
          setConfirmando(null)
          // Reactivar es el único de los dos verbos que puede subir el conteo de una empresa.
          if (verbo === 'reactivar' && esSobreCupo(err)) {
            setSobreCupo(membresia)
            return
          }
          setSobreCupo(null)
          setError(translateApiError(err))
        },
      }
    )
  }

  const columnas: Column<Membresia>[] = [
    {
      key: 'empresa',
      header: t('usuarios.membresias.empresa'),
      cell: (m) => (
        <CeldaEstado
          nombre={m.business_name}
          chip={m.es_sistema ? <Tag tone="ink">{t('usuarios.membresias.sistema')}</Tag> : undefined}
        />
      ),
    },
    {
      key: 'role',
      header: t('usuarios.membresias.rol'),
      width: 'w-36',
      cell: (m) => <CeldaEstado chip={<Tag tone="neutral">{t(`roles.${m.role}`)}</Tag>} />,
    },
    {
      key: 'status',
      header: t('usuarios.membresias.estado'),
      width: 'w-28',
      cell: (m) => (
        <CeldaEstado
          chip={
            <Tag tone={m.status === 'active' ? 'green' : 'neutral'}>
              {t(`usuarios.membresias.${m.status === 'active' ? 'activa' : 'inactiva'}`)}
            </Tag>
          }
        />
      ),
    },
    {
      key: 'vinculo',
      header: t('usuarios.membresias.vinculo'),
      width: 'w-28',
      cell: (m) => <CeldaEstado chip={<Tag tone="neutral">{t(`vinculo.${m.vinculo}`)}</Tag>} />,
    },
    {
      key: 'acto',
      header: '',
      width: 'w-32',
      className: 'text-right',
      stopClick: true,
      cell: (m) => (
        <CeldaActo
          acto={
            m.es_sistema ? undefined : m.status === 'active' ? (
              <Button
                size="sm"
                variant="outline"
                data-testid={`usuario-membresia-desactivar-${m.employee_id}`}
                disabled={cambiar.isPending}
                onClick={() => setConfirmando(m)}>
                {t('usuarios.membresias.desactivar')}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                data-testid={`usuario-membresia-reactivar-${m.employee_id}`}
                disabled={cambiar.isPending}
                onClick={() => mover(m, 'reactivar')}>
                {t('usuarios.membresias.reactivar')}
              </Button>
            )
          }
        />
      ),
    },
  ]

  return (
    <section className="space-y-2" data-testid="usuario-membresias">
      <h3 className="font-heading text-base text-foreground">{t('usuarios.membresias.title')}</h3>
      <DataTable
        columns={columnas}
        rows={usuario.membresias}
        rowKey={(m) => m.employee_id}
        emptyState={<ListEmptyState title={t('usuarios.membresias.empty')} />}
      />
      {error && (
        <p className="text-xs text-destructive" data-testid="usuario-membresias-error">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={!!confirmando}
        onOpenChange={(abierto) => !abierto && setConfirmando(null)}
        title={t('usuarios.membresias.desactivarTitle')}
        description={t('usuarios.membresias.desactivarBody')}
        confirmLabel={t('usuarios.membresias.desactivar')}
        cancelLabel={t('common.cancel')}
        pending={cambiar.isPending}
        onConfirm={() => confirmando && mover(confirmando, 'desactivar')}
      />
      <ConfirmDialog
        open={!!sobreCupo}
        onOpenChange={(abierto) => !abierto && setSobreCupo(null)}
        title={t('usuarios.membresias.sobreCupoTitle')}
        description={t('usuarios.membresias.sobreCupoBody')}
        confirmLabel={t('usuarios.membresias.sobreCupoConfirmar')}
        cancelLabel={t('common.cancel')}
        pending={cambiar.isPending}
        onConfirm={() => sobreCupo && mover(sobreCupo, 'reactivar', true)}
      />
    </section>
  )
}

/** La empresa reservada no se ofrece: su membresía es modo dios, no un empleo, y el backend la rechaza. */
export function AgregarMembresiaDialog({usuario, onClose}: {usuario: Usuario | null; onClose: () => void}) {
  const {t} = useTranslation()
  return (
    <Dialog open={!!usuario} onOpenChange={(abierto) => !abierto && onClose()}>
      {usuario && (
        <DialogContent data-testid="usuario-membresia-dialog">
          <DialogHeader>
            <DialogTitle>{t('usuarios.membresias.agregar')}</DialogTitle>
          </DialogHeader>
          {/* `key` por cuenta: el formulario nace vacío y se rehace si el diálogo se abre sobre otra persona. */}
          <AgregarMembresiaForm key={usuario.id} usuario={usuario} onClose={onClose} />
        </DialogContent>
      )}
    </Dialog>
  )
}

function AgregarMembresiaForm({usuario, onClose}: {usuario: Usuario; onClose: () => void}) {
  const {t} = useTranslation()
  const agregar = useAgregarMembresia()
  const [busqueda, setBusqueda] = useState('')
  const [page, setPage] = useState(1)
  const search = useDebouncedValue(busqueda, 300)
  const [empresa, setEmpresa] = useState<{id: string; name: string} | null>(null)
  const [rol, setRol] = useState<RolMembresia>('user')
  const [error, setError] = useState<string | null>(null)
  const [sobreCupo, setSobreCupo] = useState(false)
  const {data, isLoading} = useBusinesses({page, search, sortBy: 'name', sortDir: 'asc'})
  const empresas = (data?.items ?? []).filter((negocio) => negocio.nit !== SYSTEM_NIT)

  function enviar(confirmar_sobre_cupo?: boolean) {
    if (!empresa || validateRequired(empresa.id)) {
      setError(t('usuarios.membresias.sinEmpresa'))
      return
    }
    setError(null)
    agregar.mutate(
      {userId: usuario.id, business_id: empresa.id, role: rol, confirmar_sobre_cupo},
      {
        onSuccess: () => {
          publicarExito(t('usuarios.membresias.agregada.doneTitle'), t('usuarios.membresias.agregada.doneBody'), {
            testId: 'usuario-membresia-agregada',
          })
          onClose()
        },
        onError: (err) => {
          if (esSobreCupo(err)) {
            setSobreCupo(true)
            return
          }
          setSobreCupo(false)
          setError(translateApiError(err))
        },
      }
    )
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    enviar()
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="usuario-membresia-empresa">{t('usuarios.membresias.empresa')}</Label>
        <SearchInput
          value={busqueda}
          onChange={(valor) => {
            setBusqueda(valor)
            setPage(1)
          }}
          placeholder={t('usuarios.membresias.buscarEmpresa')}
          className="max-w-none"
          data-testid="usuario-membresia-buscar"
        />
        {isLoading && !data ? (
          <Skeleton className="h-32 w-full" />
        ) : empresas.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">{t('usuarios.membresias.sinResultados')}</p>
        ) : (
          <div
            id="usuario-membresia-empresa"
            className="flex max-h-56 flex-col overflow-y-auto rounded-lg border border-border">
            {empresas.map((negocio) => (
              <button
                key={negocio.id}
                type="button"
                aria-pressed={empresa?.id === negocio.id}
                data-testid={`usuario-membresia-empresa-${negocio.id}`}
                onClick={() => {
                  setEmpresa({id: negocio.id, name: negocio.name})
                  setError(null)
                }}
                className="flex items-center justify-between gap-3 border-b border-border px-2 py-2 text-left outline-none last:border-0 hover:bg-secondary/60 focus-visible:bg-secondary/60 aria-pressed:bg-secondary">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">{negocio.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{negocio.nit}</span>
                </span>
                {empresa?.id === negocio.id && <Tag tone="green">{t('usuarios.membresias.elegida')}</Tag>}
              </button>
            ))}
          </div>
        )}
        {!!data && data.total > BUSINESSES_PAGE_SIZE && (
          <PaginationBar page={page} pageSize={BUSINESSES_PAGE_SIZE} total={data.total} onPageChange={setPage} />
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="usuario-membresia-rol">{t('usuarios.membresias.rol')}</Label>
        <NativeSelect
          id="usuario-membresia-rol"
          data-testid="usuario-membresia-rol"
          value={rol}
          onChange={(e) => setRol(e.target.value as RolMembresia)}>
          {ROLES_MEMBRESIA.map((opcion) => (
            <option key={opcion} value={opcion}>
              {t(`roles.${opcion}`)}
            </option>
          ))}
        </NativeSelect>
      </div>

      {error && (
        <p className="text-sm text-destructive" data-testid="usuario-membresia-error">
          {error}
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" data-testid="usuario-membresia-submit" disabled={agregar.isPending}>
          {t('usuarios.membresias.agregar')}
        </Button>
      </DialogFooter>

      <ConfirmDialog
        open={sobreCupo}
        onOpenChange={(abierto) => !abierto && setSobreCupo(false)}
        title={t('usuarios.membresias.sobreCupoTitle')}
        description={t('usuarios.membresias.sobreCupoBody')}
        confirmLabel={t('usuarios.membresias.sobreCupoConfirmar')}
        cancelLabel={t('common.cancel')}
        pending={agregar.isPending}
        onConfirm={() => enviar(true)}
      />
    </form>
  )
}
